import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../config/firebase';
import { subDays } from 'date-fns';

export interface UserStats {
  total: number;
  activeLastMonth: number;
  newLastMonth: number;
}

export interface PortfolioStats {
  total: number;
  real: number;
  virtual: number;
  active: number;
  autopilotEnabled: number;
}

export interface PositionStats {
  open: number;
  pending: number;
  closed: number;
  total: number;
}

export interface WatchlistStats {
  totalEntries: number;
  activeEntries: number;
  inactiveEntries: number;
  uniqueSymbols: number;
  avgPerUser: number;
}

export interface TradingRulesStats {
  globalRules: number;
  customRules: number;
  usersWithCustomRules: number;
}

export interface PopularSymbol {
  symbol: string;
  userCount: number;
}

export interface UserUsageStats {
  userId: string;
  email: string;
  displayName?: string;
  createdAt: Date | null;
  lastActive: Date | null;
  portfolios: {
    total: number;
    real: number;
    virtual: number;
    autopilotEnabled: number;
  };
  watchlistSize: number;
  openPositions: number;
  customRules: number;
}

export interface UsageStats {
  users: UserStats;
  portfolios: PortfolioStats;
  positions: PositionStats;
  watchlist: WatchlistStats;
  tradingRules: TradingRulesStats;
  popularSymbols: PopularSymbol[];
  lastUpdated: Date;
}

export class AdminService {
  /**
   * Get all usage statistics in one call
   */
  static async getUsageStats(): Promise<UsageStats> {
    const [users, portfolios, positions, watchlist, tradingRules, popularSymbols] = await Promise.all([
      this.getUserStats(),
      this.getPortfolioStats(),
      this.getPositionStats(),
      this.getWatchlistStats(),
      this.getTradingRulesStats(),
      this.getMostPopularSymbols(10),
    ]);

    return {
      users,
      portfolios,
      positions,
      watchlist,
      tradingRules,
      popularSymbols,
      lastUpdated: new Date(),
    };
  }

  /**
   * Get user statistics
   */
  static async getUserStats(): Promise<UserStats> {
    const usersSnapshot = await getDocs(collection(db, 'userProfiles'));
    const thirtyDaysAgo = subDays(new Date(), 30);

    let activeLastMonth = 0;
    let newLastMonth = 0;

    usersSnapshot.forEach((doc) => {
      const data = doc.data();
      const updatedAt = data.updatedAt?.toDate();
      const createdAt = data.createdAt?.toDate();

      if (updatedAt && updatedAt >= thirtyDaysAgo) {
        activeLastMonth++;
      }
      if (createdAt && createdAt >= thirtyDaysAgo) {
        newLastMonth++;
      }
    });

    return {
      total: usersSnapshot.size,
      activeLastMonth,
      newLastMonth,
    };
  }

  /**
   * Get portfolio statistics
   */
  static async getPortfolioStats(): Promise<PortfolioStats> {
    const portfoliosSnapshot = await getDocs(collection(db, 'portfolios'));

    let real = 0;
    let virtual = 0;
    let active = 0;
    let autopilotEnabled = 0;

    portfoliosSnapshot.forEach((doc) => {
      const data = doc.data();

      if (data.type === 'real') {
        real++;
      } else {
        virtual++;
      }

      if (data.isActive) {
        active++;
      }

      if (data.autopilotEnabled) {
        autopilotEnabled++;
      }
    });

    return {
      total: portfoliosSnapshot.size,
      real,
      virtual,
      active,
      autopilotEnabled,
    };
  }

  /**
   * Get position statistics
   */
  static async getPositionStats(): Promise<PositionStats> {
    const positionsSnapshot = await getDocs(collection(db, 'positions'));

    let open = 0;
    let pending = 0;
    let closed = 0;

    positionsSnapshot.forEach((doc) => {
      const data = doc.data();

      switch (data.status) {
        case 'open':
          open++;
          break;
        case 'pending':
          pending++;
          break;
        case 'closed':
        case 'cancelled':
          closed++;
          break;
      }
    });

    return {
      open,
      pending,
      closed,
      total: positionsSnapshot.size,
    };
  }

  /**
   * Get watchlist statistics including unique symbols globally
   */
  static async getWatchlistStats(): Promise<WatchlistStats> {
    const watchlistSnapshot = await getDocs(collection(db, 'watchlist'));

    const uniqueSymbols = new Set<string>();
    const userSymbols = new Map<string, Set<string>>();
    let activeEntries = 0;
    let inactiveEntries = 0;

    watchlistSnapshot.forEach((doc) => {
      const data = doc.data();
      const symbol = data.symbol;
      const userId = data.userId || doc.id.split('_')[0];

      if (symbol) {
        uniqueSymbols.add(symbol);

        // Track symbols per user
        if (!userSymbols.has(userId)) {
          userSymbols.set(userId, new Set());
        }
        userSymbols.get(userId)!.add(symbol);
      }

      if (data.isTracking !== false && data.isActive !== false) {
        activeEntries++;
      } else {
        inactiveEntries++;
      }
    });

    const userCount = userSymbols.size;
    const avgPerUser = userCount > 0 ? Math.round(watchlistSnapshot.size / userCount) : 0;

    return {
      totalEntries: watchlistSnapshot.size,
      activeEntries,
      inactiveEntries,
      uniqueSymbols: uniqueSymbols.size,
      avgPerUser,
    };
  }

  /**
   * Get trading rules statistics
   */
  static async getTradingRulesStats(): Promise<TradingRulesStats> {
    const rulesSnapshot = await getDocs(collection(db, 'trading_rules'));

    let globalRules = 0;
    let customRules = 0;
    const usersWithCustom = new Set<string>();

    rulesSnapshot.forEach((doc) => {
      const data = doc.data();

      if (data.isGlobal) {
        globalRules++;
      } else {
        customRules++;
        if (data.userId) {
          usersWithCustom.add(data.userId);
        }
      }
    });

    return {
      globalRules,
      customRules,
      usersWithCustomRules: usersWithCustom.size,
    };
  }

  /**
   * Get most popular symbols tracked across all users
   */
  static async getMostPopularSymbols(limit: number = 10): Promise<PopularSymbol[]> {
    const watchlistSnapshot = await getDocs(
      query(collection(db, 'watchlist'), where('isTracking', '==', true))
    );

    // Count how many unique users track each symbol
    const symbolUsers = new Map<string, Set<string>>();

    watchlistSnapshot.forEach((doc) => {
      const data = doc.data();
      const symbol = data.symbol;
      const userId = data.userId || doc.id.split('_')[0];

      if (symbol) {
        if (!symbolUsers.has(symbol)) {
          symbolUsers.set(symbol, new Set());
        }
        symbolUsers.get(symbol)!.add(userId);
      }
    });

    // Convert to array and sort by user count
    const popularSymbols: PopularSymbol[] = [];
    symbolUsers.forEach((users, symbol) => {
      popularSymbols.push({
        symbol,
        userCount: users.size,
      });
    });

    // Sort by user count descending
    popularSymbols.sort((a, b) => b.userCount - a.userCount);

    return popularSymbols.slice(0, limit);
  }

  /**
   * Get all unique symbols tracked globally (for API rate limit monitoring)
   */
  static async getGlobalUniqueSymbols(): Promise<{ count: number; symbols: string[] }> {
    const watchlistSnapshot = await getDocs(
      query(collection(db, 'watchlist'), where('isTracking', '==', true))
    );

    const uniqueSymbols = new Set<string>();

    watchlistSnapshot.forEach((doc) => {
      const symbol = doc.data().symbol;
      if (symbol) {
        uniqueSymbols.add(symbol);
      }
    });

    const symbols = Array.from(uniqueSymbols).sort();

    return {
      count: symbols.length,
      symbols,
    };
  }

  /**
   * Get usage statistics per user
   */
  static async getPerUserStats(): Promise<UserUsageStats[]> {
    // Fetch all data in parallel
    const [usersSnapshot, portfoliosSnapshot, watchlistSnapshot, positionsSnapshot, rulesSnapshot] =
      await Promise.all([
        getDocs(collection(db, 'userProfiles')),
        getDocs(collection(db, 'portfolios')),
        getDocs(collection(db, 'watchlist')),
        getDocs(collection(db, 'positions')),
        getDocs(collection(db, 'trading_rules')),
      ]);

    // Build maps for quick lookup
    const portfoliosByUser = new Map<string, { total: number; real: number; virtual: number; autopilotEnabled: number }>();
    const watchlistByUser = new Map<string, number>();
    const positionsByUser = new Map<string, number>();
    const rulesByUser = new Map<string, number>();

    // Aggregate portfolios by user
    portfoliosSnapshot.forEach((doc) => {
      const data = doc.data();
      const userId = data.userId;
      if (!userId) return;

      if (!portfoliosByUser.has(userId)) {
        portfoliosByUser.set(userId, { total: 0, real: 0, virtual: 0, autopilotEnabled: 0 });
      }
      const stats = portfoliosByUser.get(userId)!;
      stats.total++;
      if (data.type === 'real') stats.real++;
      else stats.virtual++;
      if (data.autopilotEnabled) stats.autopilotEnabled++;
    });

    // Aggregate watchlist by user
    watchlistSnapshot.forEach((doc) => {
      const data = doc.data();
      const userId = data.userId || doc.id.split('_')[0];
      watchlistByUser.set(userId, (watchlistByUser.get(userId) || 0) + 1);
    });

    // Aggregate open positions by user (need to look up portfolio owner)
    const portfolioOwners = new Map<string, string>();
    portfoliosSnapshot.forEach((doc) => {
      const data = doc.data();
      if (data.userId) {
        portfolioOwners.set(doc.id, data.userId);
      }
    });

    positionsSnapshot.forEach((doc) => {
      const data = doc.data();
      if (data.status === 'open' || data.status === 'pending') {
        const userId = portfolioOwners.get(data.portfolioId);
        if (userId) {
          positionsByUser.set(userId, (positionsByUser.get(userId) || 0) + 1);
        }
      }
    });

    // Aggregate custom rules by user
    rulesSnapshot.forEach((doc) => {
      const data = doc.data();
      if (!data.isGlobal && data.userId) {
        rulesByUser.set(data.userId, (rulesByUser.get(data.userId) || 0) + 1);
      }
    });

    // Build user stats array
    const userStats: UserUsageStats[] = [];

    usersSnapshot.forEach((doc) => {
      const data = doc.data();
      const userId = doc.id;

      userStats.push({
        userId,
        email: data.email || 'Unknown',
        displayName: data.displayName,
        createdAt: data.createdAt?.toDate() || null,
        lastActive: data.updatedAt?.toDate() || null,
        portfolios: portfoliosByUser.get(userId) || { total: 0, real: 0, virtual: 0, autopilotEnabled: 0 },
        watchlistSize: watchlistByUser.get(userId) || 0,
        openPositions: positionsByUser.get(userId) || 0,
        customRules: rulesByUser.get(userId) || 0,
      });
    });

    // Sort by last active date (most recent first)
    userStats.sort((a, b) => {
      if (!a.lastActive && !b.lastActive) return 0;
      if (!a.lastActive) return 1;
      if (!b.lastActive) return -1;
      return b.lastActive.getTime() - a.lastActive.getTime();
    });

    return userStats;
  }
}
